/**
 * Admin REST client. Mirrors the /api/admin/* endpoints + the GDPR self-delete.
 *
 * Every call goes through `authHeaders` so it works in both dev mode (sends
 * `X-Dev-User`) and Entra mode (sends a Bearer token). Backend's
 * `_require_admin` does the role gate; we don't gate here.
 */
import { acquireToken, USE_REAL_AUTH } from "../auth/msal";
import { noteRequestId } from "../error-reporting";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

async function authHeaders(devUser?: string): Promise<Record<string, string>> {
  if (USE_REAL_AUTH) {
    const t = await acquireToken();
    return t ? { Authorization: `Bearer ${t.token}` } : {};
  }
  return devUser ? { "X-Dev-User": devUser } : {};
}

export interface AssistantRow {
  slug: string;
  name: string;
  description: string;
  system_prompt: string;
  chat_deployment: string | null;
  /** AD groups whose users can SEE the assistant + its corpus. Empty
   * = visible to everyone (legacy default, mirrors the chat
   * endpoint's "no scope, no gate" semantics). Backs the
   * `/api/assistants/visible` filter on the Hub. */
  acl_scope: string[];
  /** Corpus partition (ADR 0010). Empty = unscoped; otherwise only
   * chunks whose `scope_tags` intersect this list flow through
   * retrieval. Independent of acl_scope — one controls visibility
   * of the assistant, the other narrows what it retrieves. */
  content_scope: string[];
  /** Use a dedicated `choir-content-<slug>` index instead of the
   * shared one. Default off; flip on for very large or strictly-
   * isolated corpora (ADR 0010 §"Dedicated-index promotion"). */
  dedicated_index: boolean;
  settings: Record<string, unknown>;
  enabled: boolean;
  /** ADR 0027 release stage. `experimental` = admins only; `pilot` = named
   * pilot users and members; `production` = everyone the ACL admits.
   * Optional because older backends do not return it. */
  maturity?: "experimental" | "pilot" | "production";
}

export interface UsageRow {
  turns: number;
  prompt_tokens: number;
  completion_tokens: number;
  latency_ms_total: number;
  /** Rate-card cost of the bucket in USD. Computed server-side by summing
   * per-model token counts through the backend's rate card, so it is
   * present for EVERY grouping — not just group_by=model. */
  cost_usd: number;
  /** The same cost in the platform's display currency (`UsageResponse.currency`). */
  cost: number;
  /** Models in this bucket with no rate-card entry. Non-empty means `cost`
   * is an under-estimate for this row. */
  unpriced_models: string[];
  /** Only on group_by=user_oid, and only when the principal has a `users`
   * row: `apikey:*` principals and deleted users come back null. */
  email?: string | null;
  display_name?: string | null;
  // The grouped column rides along under its name (user_oid|assistant_slug|...)
  [k: string]: unknown;
}

export interface UsageResponse {
  group_by: string;
  rows: UsageRow[];
  /** Principals the backend left out (USAGE_INTERNAL_OIDS). Empty when
   * `includeInternal` was requested. */
  excluded_oids: string[];
  /** Display currency for `UsageRow.cost` (DKK by default). */
  currency: string;
  usd_rate: number;
  /** Models seen in the window with no rate-card entry. Non-empty means the
   * page's costs are an under-estimate AND those turns count against nobody's
   * budget — the operator needs to add them to `LLM_RATE_CARD_JSON`. */
  unpriced_models: string[];
}

// ── Spend budgets ──────────────────────────────────────────────────────────

export interface BudgetStatus {
  subject: string;
  currency: string;
  day_spend: number;
  month_spend: number;
  day_limit: number | null;
  month_limit: number | null;
  day_reset_at: string;
  month_reset_at: string;
  enforced: boolean;
  overridden: boolean;
  /** Which limit is exhausted, if any. */
  exceeded: "daily" | "monthly" | null;
  warn: boolean;
  /** 0..1+ — the fuller of the two dimensions, for a single progress bar. */
  worst_fraction: number;
}

export interface BudgetPolicy {
  enabled: boolean;
  currency: string;
  usd_rate: number;
  timezone: string;
  daily_limit: number | null;
  monthly_limit: number | null;
  warn_at: number;
  enforced: boolean;
}

export interface BudgetOverride {
  subject: string;
  email: string | null;
  display_name: string | null;
  daily_limit: number | null;
  monthly_limit: number | null;
  enforced: boolean | null;
  note: string;
  updated_at: string | null;
  updated_by: string;
  status?: BudgetStatus;
}

export interface BudgetsResponse {
  policy: BudgetPolicy;
  overrides: BudgetOverride[];
}

export interface AuditRow {
  id: number;
  ts: string;
  actor_oid: string;
  actor_email: string | null;
  action: string;
  target_kind: string;
  target_id: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  extra: Record<string, unknown> | null;
}

export class AdminError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function check(r: Response): Promise<Response> {
  // Remember the correlation id the backend stamped on this response, so a
  // later browser error can be joined to the request that preceded it
  // (ADR 0041). Every response carries it, ok or not.
  noteRequestId(r.headers.get("X-Request-Id"));
  if (r.ok) return r;
  if (r.status === 403) {
    throw new AdminError(
      "Forbidden — your account is missing the admin group.",
      403,
    );
  }
  // Read the body ONCE, then try to parse it. The previous shape called
  // `r.json()` and fell back to `r.text()` in the catch — but a failed
  // `json()` has already consumed the stream, so the fallback threw
  // "body stream already read" and that TypeError replaced the real error.
  // A 500 from the server surfaced to the user as a browser plumbing
  // message, which is exactly when you least want the error hidden.
  let detail = "";
  try {
    const body = await r.text();
    try {
      const parsed = JSON.parse(body);
      detail =
        typeof parsed?.detail === "string" ? parsed.detail : body.slice(0, 500);
    } catch {
      detail = body.slice(0, 500);
    }
  } catch {
    detail = r.statusText || "no response body";
  }
  throw new AdminError(`HTTP ${r.status}: ${detail}`, r.status);
}

// ── Assistants ─────────────────────────────────────────────────────────────

export async function listAssistants(
  devUser?: string,
  enabledOnly = false,
): Promise<AssistantRow[]> {
  const qs = enabledOnly ? "?enabled_only=true" : "";
  const r = await check(
    await fetch(`${API_BASE}/api/admin/assistants${qs}`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as AssistantRow[];
}

export async function getAssistant(
  slug: string,
  devUser?: string,
): Promise<AssistantRow> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}`,
      {
        headers: await authHeaders(devUser),
        cache: "no-store",
      },
    ),
  );
  return (await r.json()) as AssistantRow;
}

export async function upsertAssistant(
  body: AssistantRow,
  devUser?: string,
): Promise<AssistantRow> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(body.slug)}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders(devUser)),
        },
        body: JSON.stringify(body),
      },
    ),
  );
  return (await r.json()) as AssistantRow;
}

export async function disableAssistant(
  slug: string,
  devUser?: string,
): Promise<void> {
  await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}`,
      { method: "DELETE", headers: await authHeaders(devUser) },
    ),
  );
}

// ── Glossary (ADR 0026 v2) ─────────────────────────────────────────────────

export interface GlossaryEntryRow {
  slug: string;
  title: string;
  body: string;
  updated_by_oid: string | null;
  created_at: string;
  updated_at: string;
  /** Assistant slugs that include this glossary entry via
   *  `settings.glossary_includes`. Populated by the admin list + get
   *  endpoints; empty (or stale) on PUT/DELETE responses since those
   *  flows don't refresh it. The next list call repopulates. */
  used_by: string[];
}

export async function listGlossary(
  devUser?: string,
): Promise<GlossaryEntryRow[]> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/glossary`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as GlossaryEntryRow[];
}

export async function upsertGlossaryEntry(
  body: { slug: string; body: string; title?: string },
  devUser?: string,
): Promise<GlossaryEntryRow> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/glossary/${encodeURIComponent(body.slug)}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders(devUser)),
        },
        body: JSON.stringify({
          slug: body.slug,
          body: body.body,
          title: body.title ?? "",
        }),
      },
    ),
  );
  return (await r.json()) as GlossaryEntryRow;
}

export async function deleteGlossaryEntry(
  slug: string,
  devUser?: string,
): Promise<void> {
  await check(
    await fetch(`${API_BASE}/api/admin/glossary/${encodeURIComponent(slug)}`, {
      method: "DELETE",
      headers: await authHeaders(devUser),
    }),
  );
}

// ── Sources + ingest + upload (ADR 0011 + Phase 6) ────────────────────────

export interface AssistantSourceRow {
  id: number;
  assistant_slug: string;
  source_type: string;
  config: Record<string, unknown>;
  scope_tags: string[];
  enabled: boolean;
  sort_order: number;
}

export interface IngestJobRow {
  external_id: string;
  assistant_slug: string;
  status: "pending" | "running" | "succeeded" | "failed" | "cancelled";
  sources_total: number;
  sources_done: number;
  docs_indexed: number;
  docs_skipped: number;
  docs_failed: number;
  chunks_indexed: number;
  last_error: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface UploadResult {
  filename: string;
  path: string;
  bytes: number;
  source_id: number;
  job_external_id: string;
}

export async function listSources(
  slug: string,
  devUser?: string,
): Promise<AssistantSourceRow[]> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/sources`,
      { headers: await authHeaders(devUser), cache: "no-store" },
    ),
  );
  return (await r.json()) as AssistantSourceRow[];
}

export async function triggerIngest(
  slug: string,
  devUser?: string,
): Promise<IngestJobRow> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/ingest`,
      { method: "POST", headers: await authHeaders(devUser) },
    ),
  );
  return (await r.json()) as IngestJobRow;
}

export async function getIngestJob(
  externalId: string,
  devUser?: string,
): Promise<IngestJobRow> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/ingest-jobs/${encodeURIComponent(externalId)}`,
      { headers: await authHeaders(devUser), cache: "no-store" },
    ),
  );
  return (await r.json()) as IngestJobRow;
}

/**
 * Upload a single file via multipart/form-data and trigger an ingest.
 * Returns the saved filename + the ingest job's external_id. The caller
 * can poll `getIngestJob(externalId)` to follow progress.
 *
 * Backend: see `POST /api/admin/assistants/{slug}/upload`.
 */
export async function uploadFile(
  slug: string,
  file: File,
  devUser?: string,
): Promise<UploadResult> {
  const form = new FormData();
  form.append("file", file);
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/upload`,
      {
        method: "POST",
        headers: await authHeaders(devUser),
        body: form,
      },
    ),
  );
  return (await r.json()) as UploadResult;
}

// ── Sources an admin adds from the UI (ADR 0057) ───────────────────────────

export interface SourceFormField {
  key: string;
  label: string;
  kind: "lines" | "int" | "bool" | "resource" | "library";
  required: boolean;
  default: unknown;
  help: string;
  placeholder: string;
  advanced: boolean;
  min: number | null;
  max: number | null;
}

export interface SourceKind {
  key: string;
  label: string;
  description: string;
  source_type: string | null;
  mode: "form" | "upload";
  available: boolean;
  unavailable_reason: string;
  visibility: string;
  /** Connection kind this source reads through ("" = none). */
  needs_connection: string;
  fields: SourceFormField[];
}

/**
 * What runs a source added from the UI on this deployment. `in_process`
 * starts at once; `external` is the indexer's Job on a short cron, so a
 * queued sync waits for the next tick; `none` never runs it.
 */
export type IngestRunner = "in_process" | "external" | "none";

export interface SourceTypesResponse {
  kinds: SourceKind[];
  runner: IngestRunner;
  runner_available: boolean;
}

export interface ManagedSource extends AssistantSourceRow {
  /** Null when the form cannot edit this source without losing settings. */
  form: { kind: string; values: Record<string, unknown> } | null;
  warnings: string[];
}

export interface ManagedSourceGroup {
  assistant_slug: string;
  assistant_name: string;
  last_job: IngestJobRow | null;
  sources: ManagedSource[];
}

export interface ManagedSourcesResponse {
  groups: ManagedSourceGroup[];
  runner: IngestRunner;
  runner_available: boolean;
}

export async function getSourceTypes(
  devUser?: string,
): Promise<SourceTypesResponse> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/source-types`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as SourceTypesResponse;
}

export async function getManagedSources(
  devUser?: string,
): Promise<ManagedSourcesResponse> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/managed-sources`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as ManagedSourcesResponse;
}

export interface AddSourceResult {
  source: AssistantSourceRow;
  warnings: string[];
  /** ADR 0053 — the assistant's access class after adding, and whether this
   * source is what changed it. Null when governance could not be read. */
  access_class: {
    key: string;
    label: string;
    changed: boolean;
    was: string | null;
    route: string;
  } | null;
}

export async function addSourceFromForm(
  slug: string,
  body: { kind: string; values: Record<string, unknown> },
  devUser?: string,
): Promise<AddSourceResult> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/sources/from-form`,
      {
        method: "POST",
        headers: {
          ...(await authHeaders(devUser)),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      },
    ),
  );
  return (await r.json()) as AddSourceResult;
}

export async function updateSource(
  slug: string,
  sourceId: number,
  patch: { values?: Record<string, unknown>; enabled?: boolean },
  devUser?: string,
): Promise<AssistantSourceRow> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/sources/${sourceId}`,
      {
        method: "PATCH",
        headers: {
          ...(await authHeaders(devUser)),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(patch),
      },
    ),
  );
  return (await r.json()) as AssistantSourceRow;
}

export async function deleteSource(
  slug: string,
  sourceId: number,
  devUser?: string,
): Promise<{ deleted: boolean; chunks_purged: number }> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/sources/${sourceId}`,
      { method: "DELETE", headers: await authHeaders(devUser) },
    ),
  );
  return (await r.json()) as { deleted: boolean; chunks_purged: number };
}

// ── What an assistant reads, as named sources rather than tags ─────────────

export interface AssistantRead {
  /** What it is, in the words a person would use. */
  title: string;
  detail: string;
  enforcement: AccessClassSource["enforcement"];
  /** The tag or table behind it — for the few who want it. */
  via: string;
  /** Added from Data sources, so it can be changed there. */
  managed: boolean;
}

export interface AssistantReadsResponse {
  slug: string;
  access_class: { key: string; label: string };
  reads: AssistantRead[];
}

export async function getAssistantReads(
  slug: string,
  devUser?: string,
): Promise<AssistantReadsResponse> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/reads`,
      { headers: await authHeaders(devUser), cache: "no-store" },
    ),
  );
  return (await r.json()) as AssistantReadsResponse;
}

/** What an assistant reads BEYOND its scope tags — fact tables and tools.
 *  The overview shows scope tags already; these are the ones it was missing
 *  (ADR 0059). */
export interface ReadsSummary {
  access_class: { key: string; label: string };
  beyond_corpus: { title: string; via: string; enforcement: string }[];
  /** Everything it reads, by name, once each. */
  reads?: { title: string; enforcement: string }[];
}

export async function getAssistantsReadsSummary(
  devUser?: string,
): Promise<Record<string, ReadsSummary>> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/assistants/reads-summary`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  const body = (await r.json()) as { summaries: Record<string, ReadsSummary> };
  return body.summaries ?? {};
}

// ── Connections and what they have been granted (ADR 0057 §1) ─────────────

export interface ProbeCheck {
  name: string;
  /** null = not run, because an earlier check failed. */
  ok: boolean | null;
  message: string;
}

export interface GrantedResource {
  id: number;
  connection_key: string;
  address: string;
  /** The probe's verdict — never an admin's claim. */
  status: "unverified" | "granted" | "not_granted" | "not_found" | "error";
  title: string;
  drives: { id: string; name: string }[];
  checks: ProbeCheck[];
  last_error: string | null;
  last_probed_at: string | null;
  created_by: string;
  used_by: number;
  /** Set for sites `config/connections.yaml` declares: what reads them. */
  note: string;
  /** A scheduled ingest reads it, so it cannot be taken off the list. */
  declared: boolean;
  /** Present whenever access is not proven: what to send the tenant admin. */
  grant: { pnp: string; graph: string } | null;
}

export interface ConnectionRow {
  key: string;
  label: string;
  kind: string;
  app_name: string;
  tenant_id: string;
  client_id: string;
  permission_model: string;
  granted_by: string;
  resources: GrantedResource[];
}

export interface ConnectionsResponse {
  connections: ConnectionRow[];
  /** False where nothing will ever check what is added. */
  probing: boolean;
}

export async function getConnections(
  devUser?: string,
): Promise<ConnectionsResponse> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/connections`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as ConnectionsResponse;
}

export async function addGrantedResource(
  connectionKey: string,
  url: string,
  devUser?: string,
): Promise<GrantedResource> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/connections/${encodeURIComponent(connectionKey)}/resources`,
      {
        method: "POST",
        headers: {
          ...(await authHeaders(devUser)),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ url }),
      },
    ),
  );
  return (await r.json()) as GrantedResource;
}

export async function recheckGrantedResource(
  id: number,
  devUser?: string,
): Promise<GrantedResource> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/granted-resources/${id}/recheck`, {
      method: "POST",
      headers: await authHeaders(devUser),
    }),
  );
  return (await r.json()) as GrantedResource;
}

export async function deleteGrantedResource(
  id: number,
  devUser?: string,
): Promise<void> {
  await check(
    await fetch(`${API_BASE}/api/admin/granted-resources/${id}`, {
      method: "DELETE",
      headers: await authHeaders(devUser),
    }),
  );
}

// ── Corpus-aware system prompt (ADR 0014) ─────────────────────────────────

export interface GeneratePromptResult {
  prompt: string;
  applied: boolean;
  n_hits: number;
  sample_query: string;
}

export async function generatePrompt(
  slug: string,
  opts: { apply?: boolean; sampleSize?: number; sampleQuery?: string },
  devUser?: string,
): Promise<GeneratePromptResult> {
  const body: Record<string, unknown> = {
    apply: opts.apply ?? false,
    sample_size: opts.sampleSize ?? 8,
  };
  if (opts.sampleQuery) body.sample_query = opts.sampleQuery;
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/generate-prompt`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders(devUser)),
        },
        body: JSON.stringify(body),
      },
    ),
  );
  return (await r.json()) as GeneratePromptResult;
}

// ── ADR 0038 — SME co-development (refine / revisions / rollback) ─────────

export interface RefinePromptResult {
  proposed_prompt: string;
  current_prompt: string;
  rationale: string;
  applied: boolean;
  revision_id: number | null;
}

export async function refinePrompt(
  slug: string,
  opts: {
    instruction: string;
    apply?: boolean;
    includeFeedback?: boolean;
    /** Apply-what-was-reviewed: send the approved text back verbatim so
     * the backend applies exactly it (no second LLM generation). */
    proposedPrompt?: string;
    /** V2: target a workflow body instead of the system prompt. */
    workflow?: string;
  },
  devUser?: string,
): Promise<RefinePromptResult> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/refine-prompt`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders(devUser)),
        },
        body: JSON.stringify({
          instruction: opts.instruction,
          apply: opts.apply ?? false,
          include_feedback: opts.includeFeedback ?? true,
          ...(opts.proposedPrompt
            ? { proposed_prompt: opts.proposedPrompt }
            : {}),
          ...(opts.workflow ? { workflow: opts.workflow } : {}),
        }),
      },
    ),
  );
  return (await r.json()) as RefinePromptResult;
}

export interface AssistantRevisionRow {
  id: number;
  created_at: string;
  changed_by_oid: string;
  change_note: string;
  /** "" = system-prompt revision; otherwise the workflow name. */
  workflow_name: string;
  prompt_chars: number;
}

export interface AssistantWorkflowRow {
  name: string;
  /** Effective body — DB override when `overridden`, else the file text. */
  body: string;
  overridden: boolean;
  /** Regex patterns that route a turn to this workflow. */
  triggers?: string[];
}

export async function listWorkflows(
  slug: string,
  devUser?: string,
): Promise<AssistantWorkflowRow[]> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/workflows`,
      { headers: await authHeaders(devUser) },
    ),
  );
  return (await r.json()) as AssistantWorkflowRow[];
}

export async function resetWorkflow(
  slug: string,
  name: string,
  devUser?: string,
): Promise<void> {
  await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/workflows/${encodeURIComponent(name)}`,
      { method: "DELETE", headers: await authHeaders(devUser) },
    ),
  );
}

export async function listRevisions(
  slug: string,
  devUser?: string,
): Promise<AssistantRevisionRow[]> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/revisions`,
      { headers: await authHeaders(devUser) },
    ),
  );
  return (await r.json()) as AssistantRevisionRow[];
}

export async function rollbackRevision(
  slug: string,
  revisionId: number,
  devUser?: string,
): Promise<void> {
  await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/revisions/${revisionId}/rollback`,
      { method: "POST", headers: await authHeaders(devUser) },
    ),
  );
}

// ── Usage ──────────────────────────────────────────────────────────────────

export async function getUsage(
  groupBy: "user_oid" | "assistant_slug" | "model",
  devUser?: string,
  fromTs?: string,
  toTs?: string,
  includeInternal = true,
): Promise<UsageResponse> {
  const qs = new URLSearchParams({ group_by: groupBy });
  if (fromTs) qs.set("from_ts", fromTs);
  if (toTs) qs.set("to_ts", toTs);
  // Default TRUE, unlike the backend. The API defaults to hiding
  // USAGE_INTERNAL_OIDS so an integration asking "how is the product being
  // used" gets that answer; the admin page's question is "what are we
  // spending", where leaving a principal out is how a bill surprises you.
  if (includeInternal) qs.set("include_internal", "true");
  const r = await check(
    await fetch(`${API_BASE}/api/admin/usage?${qs.toString()}`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as UsageResponse;
}

// ── Content-scope tags ─────────────────────────────────────────────────────

export interface ScopeTagSource {
  assistant_slug: string;
  source_type: string;
  /** Redacted "where does this point" — allowlisted config keys only. */
  location: string;
  enabled: boolean;
}

export interface ScopeTagInfo {
  tag: string;
  /** Assistants narrowed to this tag. */
  assistants: string[];
  /** Sources that stamp this tag onto documents at ingest, from the
   * `assistant_sources` table. Empty on deployments that drive ingest from
   * the checked-in indexer YAML instead — the registry fields below carry
   * the answer there. */
  sources: ScopeTagSource[];
  /** False when `config/scope-tags.yaml` has no entry for this tag. */
  documented: boolean;
  /** Human name, e.g. "Bid Office document library". */
  label: string;
  /** Originating system, e.g. "SharePoint", "Risma", "Business Central". */
  system: string;
  /** "live" = something ingests into it; "reserved" = nothing does yet. */
  status: string;
  /** Where the content comes from, in human terms. */
  where: string;
  /** One-line hover-card copy. */
  summary: string;
  /** One-line access note — who can actually retrieve this. */
  access: string;
  /** Long form, for the config file rather than the tooltip. */
  description: string;
  /** ACL or status warning that decides whether a scoped assistant actually
   * retrieves anything. */
  caveat: string;
}

export async function getScopeTags(devUser?: string): Promise<ScopeTagInfo[]> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/scope-tags`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return ((await r.json()) as { tags: ScopeTagInfo[] }).tags;
}

// ── Spend budgets ──────────────────────────────────────────────────────────

export async function getBudgets(devUser?: string): Promise<BudgetsResponse> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/budgets`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as BudgetsResponse;
}

export async function saveBudgetPolicy(
  policy: BudgetPolicy,
  devUser?: string,
): Promise<BudgetPolicy> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/budgets/policy`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(await authHeaders(devUser)),
      },
      body: JSON.stringify(policy),
    }),
  );
  return (await r.json()) as BudgetPolicy;
}

/** `subject` may be a principal id (Entra oid, `apikey:<n>`) or an email,
 * which the backend resolves against the user table. */
export async function saveBudgetOverride(
  subject: string,
  body: {
    daily_limit: number | null;
    monthly_limit: number | null;
    enforced: boolean | null;
    note: string;
  },
  devUser?: string,
): Promise<void> {
  await check(
    await fetch(
      `${API_BASE}/api/admin/budgets/${encodeURIComponent(subject)}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders(devUser)),
        },
        body: JSON.stringify(body),
      },
    ),
  );
}

export async function deleteBudgetOverride(
  subject: string,
  devUser?: string,
): Promise<void> {
  await check(
    await fetch(
      `${API_BASE}/api/admin/budgets/${encodeURIComponent(subject)}`,
      { method: "DELETE", headers: await authHeaders(devUser) },
    ),
  );
}

/** The caller's OWN meter — no admin role required. `enabled:false` means
 * budgeting is switched off platform-wide and the UI renders nothing. */
export async function getMyBudget(
  devUser?: string,
): Promise<
  ({ enabled: true; warn_at: number } & BudgetStatus) | { enabled: false }
> {
  const r = await check(
    await fetch(`${API_BASE}/api/me/budget`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return await r.json();
}

// ── Audit ──────────────────────────────────────────────────────────────────

export async function getAudit(
  filters: {
    actorOid?: string;
    action?: string;
    fromTs?: string;
    toTs?: string;
    limit?: number;
  },
  devUser?: string,
): Promise<AuditRow[]> {
  const qs = new URLSearchParams();
  if (filters.actorOid) qs.set("actor_oid", filters.actorOid);
  if (filters.action) qs.set("action", filters.action);
  if (filters.fromTs) qs.set("from_ts", filters.fromTs);
  if (filters.toTs) qs.set("to_ts", filters.toTs);
  if (filters.limit) qs.set("limit", String(filters.limit));
  const url = qs.toString()
    ? `${API_BASE}/api/admin/audit?${qs.toString()}`
    : `${API_BASE}/api/admin/audit`;
  const r = await check(
    await fetch(url, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as AuditRow[];
}

// ── Scheduled jobs (ADR 0041) ──────────────────────────────────────────────

export interface JobExecutionRow {
  name: string;
  status: string;
  start: string;
}

export interface JobRow {
  name: string;
  trigger_type: string;
  cron: string;
  /** True when the schedule says it should have fired and it didn't.
   * This is the signal logs cannot provide — a job that never starts
   * emits nothing, so silence looks identical to success. */
  overdue: boolean;
  reason: string;
  expected_last_run: string;
  last_status: string;
  last_start: string;
  last_end: string;
  recent: JobExecutionRow[];
  recent_failures: number;
  /** A failure that still stands. For an event-triggered job, a failed
   * execution that a later or simultaneous one recovered is not open. */
  failure_open?: boolean;
  /** The most recent failed execution, whether or not it still stands. */
  failed_execution?: { name: string; start: string } | null;
}

export interface JobsResponse {
  /** False on deployments with no ARM to query (on-prem); `reason` explains. */
  available: boolean;
  reason: string;
  resource_group?: string;
  overdue_count?: number;
  jobs: JobRow[];
}

export async function getJobs(devUser?: string): Promise<JobsResponse> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/jobs`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as JobsResponse;
}

// ── Scheduled jobs (ADR 0071) ─────────────────────────────────────────────

/** The effective access a run read with — recipients, who was dropped and
 *  why, the groups and row keys left after narrowing. Keys, never data. */
export interface ScheduledJobAccess {
  job: string;
  runnable: boolean;
  reason: string;
  recipients: { email: string; via: string }[];
  dropped: { email: string; reason: string }[];
  table_groups: string[];
  row_keys: Record<string, string[]>;
}

export interface ScheduledJobRun {
  id: number;
  trigger: "schedule" | "manual" | string;
  requested_by: string;
  /** pending → running → succeeded | failed | skipped */
  status: string;
  scheduled_for: string | null;
  started_at: string | null;
  finished_at: string | null;
  /** Why it failed or was skipped. A viewer gets any answer quote redacted. */
  error: string;
  cost_dkk: number | null;
  expect: { passed: boolean; problems: string[] } | null;
  model: string | null;
  tokens: [number | null, number | null] | null;
  workflows: string[];
  tools_called: string[];
  tools_dropped: { tool: string; why: string }[];
  documents: number;
  /** How many recipients got it as a conversation. */
  delivered: number;
  access: ScheduledJobAccess | null;
  ledger_id: number | null;
}

/** invalid > disabled (in its file) > expired > paused > off (platform) > active */
export type ScheduledJobStatus =
  | "invalid"
  | "disabled"
  | "expired"
  | "paused"
  | "off"
  | "active";

export interface ScheduledJob {
  slug: string;
  source: string;
  status: ScheduledJobStatus;
  problems: string[];
  name: string;
  purpose: string;
  assistant: string;
  owner: string;
  requested_by: string;
  schedule: string;
  /** ISO with the Europe/Copenhagen offset. */
  next_fire: string | null;
  will_run: boolean;
  recipients: string[];
  declared_access: {
    groups: string[];
    row_scopes: Record<string, string[] | "all-ingested">;
  } | null;
  expect: Record<string, unknown>;
  max_cost_dkk: number | null;
  review_by: string | null;
  enabled: boolean;
  paused: boolean;
  paused_reason: string;
  paused_at: string | null;
  consecutive_failures: number;
  last_scheduled_for: string | null;
  runs: ScheduledJobRun[];
  last_access: ScheduledJobAccess | null;
}

export interface ScheduledJobsResponse {
  /** JOBS_ENABLED — the platform-wide switch. */
  jobs_enabled: boolean;
  /** Whether jobs-cron exists here; null when the backend was not told. */
  cron_wired: boolean | null;
  jobs_dir: string;
  timezone: string;
  jobs: ScheduledJob[];
}

async function scheduledJobsFetch<T>(
  path: string,
  init: RequestInit,
  devUser?: string,
): Promise<T> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/scheduled-jobs${path}`, {
      cache: "no-store",
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(await authHeaders(devUser)),
      },
    }),
  );
  return (await r.json()) as T;
}

export function getScheduledJobs(
  devUser?: string,
): Promise<ScheduledJobsResponse> {
  return scheduledJobsFetch("", {}, devUser);
}

export function getScheduledJobRuns(
  slug: string,
  opts: { limit?: number; before?: number | null } = {},
  devUser?: string,
): Promise<{ slug: string; runs: ScheduledJobRun[]; next_before: number | null }> {
  const q = new URLSearchParams({ limit: String(opts.limit ?? 20) });
  if (opts.before != null) q.set("before", String(opts.before));
  return scheduledJobsFetch(
    `/${encodeURIComponent(slug)}/runs?${q.toString()}`,
    {},
    devUser,
  );
}

export function pauseScheduledJob(
  slug: string,
  reason: string,
  devUser?: string,
): Promise<{ slug: string; paused: boolean; paused_reason: string }> {
  return scheduledJobsFetch(
    `/${encodeURIComponent(slug)}/pause`,
    { method: "POST", body: JSON.stringify({ reason }) },
    devUser,
  );
}

export function resumeScheduledJob(
  slug: string,
  devUser?: string,
): Promise<{ slug: string; paused: boolean; paused_reason: string }> {
  return scheduledJobsFetch(
    `/${encodeURIComponent(slug)}/resume`,
    { method: "POST" },
    devUser,
  );
}

export function runScheduledJobNow(
  slug: string,
  devUser?: string,
): Promise<{ slug: string; run_id: number; status: string }> {
  return scheduledJobsFetch(
    `/${encodeURIComponent(slug)}/run`,
    { method: "POST" },
    devUser,
  );
}

// ── Data sources (ADR 0055) ───────────────────────────────────────────────

export type DataSourceKind =
  | "documents"
  | "facts"
  | "documents+facts"
  | "user-content";

/** What the agreement says about itself, read from its own markdown. */
export interface AgreementMeta {
  id: string;
  module: string;
  version: string;
  effective_date: string;
  owner: string;
  between: string;
  /** Marked items still open, by the agreement's three colours. */
  open: { assumed: number; needs_review: number; needs_data: number };
  /** The open items that stop a signature, in the agreement's own words. */
  decisions: { kind: "needs_review" | "needs_data"; text: string }[];
  /** The sign-off table, with signatures recorded in the product laid over. */
  signers: AgreementSigner[];
  signatures: { needed: number; dated: number };
  signed: boolean;
}

/** A signature recorded in the product (never the document bytes). */
export interface RecordedSignature {
  id: number;
  role: string;
  signer_name: string;
  signed_on: string;
  agreement_id: string;
  agreement_version: string;
  note: string;
  has_document: boolean;
  document_name: string;
  document_sha256: string;
  /** `self`: the signer signed in DOIT AI; `recorded`: an admin recorded it. */
  method?: "self" | "recorded";
  recorded_by_email: string;
  recorded_at: string;
}

/** Someone who holds a role. No e-mail = a placeholder name. */
export interface RolePerson {
  name: string;
  email: string;
}

export interface AgreementSigner {
  role: string;
  name: string;
  /** Set when signed — in the document, or recorded here. */
  signed_on: string;
  recorded: RecordedSignature | null;
  /** A signature given for an earlier version of the text. */
  previous?: RecordedSignature;
  /** Who holds this signer role (Admin → Compliance → Responsibilities). */
  people?: RolePerson[];
}

/** exempt = none needed, by decision · missing = the file is not there ·
 * blocked = a value nobody can supply yet · draft · signed. */
export type AgreementState =
  | "exempt"
  | "missing"
  | "blocked"
  | "draft"
  | "signed";

export interface DataSourceCorpus {
  tag: string;
  label: string;
  status: string;
  enforcement: string;
  assistants: string[];
}

export interface DataSourceTable {
  table: string;
  row_security: boolean;
  scope_var: string;
  access_groups: string[];
  assistants: string[];
}

export interface DataSource {
  key: string;
  label: string;
  system: string;
  kind: DataSourceKind;
  summary: string;
  where: string;
  credential: string;
  owner: string;
  refresh: string;
  plugins: string[];
  manifests: string[];
  /** Declared in the registry; `corpora` / `tables` are what is live. */
  scope_tags: string[];
  fact_tables: string[];
  jobs: JobRow[];
  job_tokens: string[];
  /** The worst thing true of any of its jobs. */
  health: "failing" | "overdue" | "ok" | "none";
  corpora: DataSourceCorpus[];
  tables: DataSourceTable[];
  assistants: string[];
  agreement: string;
  agreement_exempt: string;
  agreement_meta: AgreementMeta | null;
  agreement_state: AgreementState;
  /** Who holds this source's data-owner role. */
  data_owner_people?: RolePerson[];
  /** ADR 0067 — how the source decides who sees what. */
  access_model?: AccessModel;
  /** DOIT AI enforces that model today. */
  mirrored?: boolean;
  access_how?: string;
}

export type AccessModel = "record" | "system" | "open" | "undecided";

export interface PlatformJob extends JobRow {
  label: string;
  summary: string;
}

export interface DataSourcesResponse {
  sources: DataSource[];
  platform_jobs: PlatformJob[];
  /** Running, but described by nobody — never hidden. */
  other_jobs: JobRow[];
  jobs_available: boolean;
  jobs_reason: string;
  jobs_origin: "azure" | "reported";
  resource_group: string;
  /** slug → display name, so nothing has to show a slug. */
  assistant_names: Record<string, string>;
}

export interface AgreementResponse {
  key: string;
  label: string;
  file: string;
  meta: AgreementMeta;
  markdown: string;
}

export async function getDataSources(
  devUser?: string,
): Promise<DataSourcesResponse> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/data-sources`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as DataSourcesResponse;
}

export async function getDataSourceAgreement(
  key: string,
  devUser?: string,
): Promise<AgreementResponse> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/data-sources/${encodeURIComponent(key)}/agreement`,
      { headers: await authHeaders(devUser), cache: "no-store" },
    ),
  );
  return (await r.json()) as AgreementResponse;
}

export async function recordAgreementSignature(
  key: string,
  body: {
    role: string;
    signer_name: string;
    signed_on: string;
    note: string;
    file: File | null;
  },
  devUser?: string,
): Promise<DataSource> {
  const form = new FormData();
  form.set("role", body.role);
  form.set("signer_name", body.signer_name);
  form.set("signed_on", body.signed_on);
  form.set("note", body.note);
  if (body.file) form.set("file", body.file);
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/data-sources/${encodeURIComponent(key)}/signatures`,
      { method: "POST", headers: await authHeaders(devUser), body: form },
    ),
  );
  return (await r.json()) as DataSource;
}

export async function revokeAgreementSignature(
  key: string,
  id: number,
  reason: string,
  devUser?: string,
): Promise<DataSource> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/data-sources/${encodeURIComponent(key)}/signatures/${id}/revoke`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders(devUser)),
        },
        body: JSON.stringify({ reason }),
      },
    ),
  );
  return (await r.json()) as DataSource;
}

export async function getSignedCopy(
  key: string,
  id: number,
  devUser?: string,
): Promise<Blob> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/data-sources/${encodeURIComponent(key)}/signatures/${id}/document`,
      { headers: await authHeaders(devUser) },
    ),
  );
  return await r.blob();
}

// ── Responsibilities (who signs, owns and decides) ─────────────────────────

export interface ResponsibilityRole {
  key: string;
  kind: "signer" | "data-owner" | "board";
  label: string;
  /** Source keys whose agreements this role appears in. */
  agreements: string[];
  people: RolePerson[];
  /** Where the names come from: saved here, the placeholder file, or the
   * agreement/registry text itself. */
  source: "admin" | "config" | "document";
  /** At least one holder has an e-mail and can act in the app. */
  named: boolean;
}

export interface ResponsibilitiesResponse {
  roles: ResponsibilityRole[];
  known_users: { email: string; name: string }[];
}

export async function getResponsibilities(
  devUser?: string,
): Promise<ResponsibilitiesResponse> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/responsibilities`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as ResponsibilitiesResponse;
}

export async function setResponsibility(
  key: string,
  people: RolePerson[],
  devUser?: string,
): Promise<{ roles: ResponsibilityRole[] }> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/responsibilities/${encodeURIComponent(key)}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders(devUser)),
        },
        body: JSON.stringify({ people }),
      },
    ),
  );
  return (await r.json()) as { roles: ResponsibilityRole[] };
}

// ── My sign-offs (any signed-in person; only their own items) ──────────────

export interface MyAgreementItem {
  source_key: string;
  source: string;
  system: string;
  agreement_id: string;
  version: string;
  role: string;
  blocked: boolean;
  decisions: { kind: "needs_review" | "needs_data"; text: string }[];
  assumed: number;
  signed_on: string;
  recorded: RecordedSignature | null;
  previous?: RecordedSignature | null;
}

export interface MyConfirmItem {
  slug: string;
  assistant: string;
  source_key: string;
  source: string;
  /** The restricted parts of that source the assistant reads. */
  parts?: string[];
  groups: string[];
  also_covers: string[];
  confirmed: boolean;
  stale: boolean;
  /** Confirmed, but before the open review round began. */
  due_in_round?: boolean;
  /** Who opens the assistant, as test personas (group:…, team:…). */
  audience?: string[];
  confirmed_by: string;
  confirmed_at: string;
  checks?: MyChecks;
}

/** An assistant's access tests, as a non-admin sees them: verdicts, not
 * the titles of what was found. */
export interface MyChecks {
  summary: AccessTestsSummary;
  tests: Pick<
    AccessTest,
    "id" | "question" | "look_for" | "personas" | "source_key"
  >[];
  results: AccessTestResult[];
}

export interface MyAcceptItem {
  slug: string;
  assistant: string;
  maturity: string;
  intended_use: string;
  risk_class: string;
  groups: string[];
  readiness: Readiness;
  checks: MyChecks;
  accepted: boolean;
  accepted_by: string;
  accepted_at: string | null;
  valid_until: string | null;
}

export interface MySignOffs {
  email: string;
  roles: string[];
  to_sign: MyAgreementItem[];
  signed: MyAgreementItem[];
  to_confirm: MyConfirmItem[];
  confirmed: MyConfirmItem[];
  /** ADR 0065 — joining/leaving the teams this person owns. */
  team_requests: TeamMembershipRequest[];
  /** ADR 0066 — AI Board members only. */
  to_accept?: MyAcceptItem[];
  accepted?: MyAcceptItem[];
  review_round?: ReviewRound | null;
  count: number;
}

export async function getMySignOffs(devUser?: string): Promise<MySignOffs> {
  const r = await check(
    await fetch(`${API_BASE}/api/my/sign-offs`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as MySignOffs;
}

export async function getMyAgreement(
  key: string,
  devUser?: string,
): Promise<AgreementResponse> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/my/sign-offs/agreements/${encodeURIComponent(key)}`,
      { headers: await authHeaders(devUser), cache: "no-store" },
    ),
  );
  return (await r.json()) as AgreementResponse;
}

export async function signMyAgreement(
  key: string,
  role: string,
  devUser?: string,
): Promise<MySignOffs> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/my/sign-offs/agreements/${encodeURIComponent(key)}/sign`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders(devUser)),
        },
        body: JSON.stringify({ role, accept: true }),
      },
    ),
  );
  return (await r.json()) as MySignOffs;
}

export async function confirmMyAccess(
  slug: string,
  note: string,
  devUser?: string,
  sourceKey?: string,
): Promise<MySignOffs> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/my/sign-offs/confirm/${encodeURIComponent(slug)}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders(devUser)),
        },
        body: JSON.stringify({ note, source_key: sourceKey }),
      },
    ),
  );
  return (await r.json()) as MySignOffs;
}

export function acceptMyAssistant(
  slug: string,
  note: string,
  devUser?: string,
): Promise<MySignOffs> {
  return _jsonCall(
    `${API_BASE}/api/my/sign-offs/accept/${encodeURIComponent(slug)}`,
    "POST",
    { accept: true, note },
    devUser,
  );
}

/** A data owner adds a test to an assistant that reads their source. */
export function addMyAccessTest(
  slug: string,
  body: AccessTestBody & { source_key: string },
  devUser?: string,
): Promise<MySignOffs> {
  return _jsonCall(
    `${API_BASE}/api/my/sign-offs/tests/${encodeURIComponent(slug)}`,
    "POST",
    body,
    devUser,
  );
}

// ── Teams (who can use an assistant, and who it is for) ────────────────────

export interface TeamMember {
  email: string;
  name: string;
  /** Set once they have signed in; empty for someone added in advance. */
  oid: string;
  added_by: string;
  added_at: string;
}

export interface Team {
  slug: string;
  name: string;
  description: string;
  members: TeamMember[];
  /** Assistants this team may use (its `team:<slug>` is in their acl_scope). */
  access: string[];
  /** Assistants made for this team — shown first on their Hub. */
  made_for: string[];
  /** ADR 0064 — data classes the team may read, each with who confirmed it. */
  clearances: TeamClearance[];
  /** ADR 0065/0074 — the Entra group that IS the membership; "" = local team. */
  entra_group_id: string;
  entra_group_name: string;
  /** ADR 0074 — "local": members kept here (a test team during the
   * transition); "entra": the linked group, read-only — DOIT AI never
   * changes it; "entra_write": ADR 0065 owner-approved writes (unused). */
  membership_mode: TeamMembershipMode;
  /** E-mails of the people who approve joining and leaving (entra_write). */
  owners: string[];
  /** Linked team: who is in the Entra group now, nested groups included. */
  entra_members: { email: string; name: string; oid: string }[];
  /** Linked team: the group's owners in Entra — who to ask about joining. */
  entra_owners: { email: string; name: string; oid: string }[];
  /** Why the Entra members could not be read, if they could not. */
  entra_error: string;
  requests: TeamMembershipRequest[];
}

export type TeamMembershipMode = "local" | "entra" | "entra_write";

export interface TeamMembershipRequest {
  id: number;
  team: string;
  team_name: string;
  entra_group_id: string;
  entra_group_name: string;
  membership_mode: TeamMembershipMode;
  owners: string[];
  action: "add" | "remove";
  email: string;
  user_oid: string;
  reason: string;
  /** pending → rejected | applied | failed (the Entra write did not go
   * through); closed — the team became read-only (ADR 0074). */
  status: "pending" | "approved" | "rejected" | "applied" | "failed" | "closed";
  requested_by: string;
  requested_at: string;
  decided_by: string;
  decided_at: string;
  decision_note: string;
  applied_at: string;
  error: string;
  can_decide?: boolean;
}

export interface TeamClearance {
  token: string;
  data_owner: string;
  note: string;
  granted_by: string;
  granted_at: string;
}

/** A reviewed data class (config/clearances.yaml) and what it unlocks today. */
export interface ClearanceClass {
  token: string;
  label: string;
  sources: string[];
  unlocks: string;
  tags: string[];
  tables: string[];
  assistants: string[];
}

export interface TeamsResponse {
  teams: Team[];
  assistants: { slug: string; name: string }[];
  known_users: { email: string; name: string }[];
  clearance_classes: ClearanceClass[];
}

async function teamsFetch<T>(
  path: string,
  init: RequestInit,
  devUser?: string,
): Promise<T> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/teams${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(await authHeaders(devUser)),
      },
    }),
  );
  return (await r.json()) as T;
}

export function getTeams(devUser?: string): Promise<TeamsResponse> {
  return teamsFetch("", { cache: "no-store" }, devUser);
}

export function createTeam(
  body: { name: string; description: string },
  devUser?: string,
): Promise<TeamsResponse> {
  return teamsFetch(
    "",
    { method: "POST", body: JSON.stringify(body) },
    devUser,
  );
}

export function updateTeam(
  slug: string,
  body: {
    name?: string;
    description?: string;
    entra_group_id?: string;
    membership_mode?: "entra" | "entra_write";
    owners?: string[];
  },
  devUser?: string,
): Promise<TeamsResponse> {
  return teamsFetch(
    `/${encodeURIComponent(slug)}`,
    { method: "PATCH", body: JSON.stringify(body) },
    devUser,
  );
}

export function deleteTeam(
  slug: string,
  devUser?: string,
): Promise<TeamsResponse> {
  return teamsFetch(
    `/${encodeURIComponent(slug)}`,
    { method: "DELETE" },
    devUser,
  );
}

export function addTeamMember(
  slug: string,
  body: { email: string; name?: string; reason?: string },
  devUser?: string,
): Promise<TeamsResponse> {
  return teamsFetch(
    `/${encodeURIComponent(slug)}/members`,
    { method: "POST", body: JSON.stringify(body) },
    devUser,
  );
}

export function removeTeamMember(
  slug: string,
  email: string,
  devUser?: string,
): Promise<TeamsResponse> {
  return teamsFetch(
    `/${encodeURIComponent(slug)}/members/${encodeURIComponent(email)}`,
    { method: "DELETE" },
    devUser,
  );
}

export function setTeamAssistants(
  slug: string,
  body: { access: string[]; made_for: string[] },
  devUser?: string,
): Promise<TeamsResponse> {
  return teamsFetch(
    `/${encodeURIComponent(slug)}/assistants`,
    { method: "PUT", body: JSON.stringify(body) },
    devUser,
  );
}

export interface EntraGroupHit {
  id: string;
  name: string;
  description: string;
  mail: string;
}

/** ADR 0074 — existing Entra groups whose name starts with `q` (with
 * nothing typed: the "Team…" groups). Admin only, read-only. */
export async function searchEntraGroups(
  q: string,
  devUser?: string,
): Promise<{ groups: EntraGroupHit[]; error: string }> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/entra-groups?q=${encodeURIComponent(q)}`,
      { cache: "no-store", headers: await authHeaders(devUser) },
    ),
  );
  return (await r.json()) as { groups: EntraGroupHit[]; error: string };
}

/** Ask to add or remove someone (yourself, or anyone as an owner/admin). */
export async function requestTeamChange(
  slug: string,
  body: { action: "add" | "remove"; email?: string; reason?: string },
  devUser?: string,
): Promise<TeamMembershipRequest> {
  const r = await check(
    await fetch(`${API_BASE}/api/teams/${encodeURIComponent(slug)}/requests`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await authHeaders(devUser)),
      },
      body: JSON.stringify(body),
    }),
  );
  return ((await r.json()) as { request: TeamMembershipRequest }).request;
}

/** An owner's decision. Approving writes the Entra group; approving a
 * failed request retries the write. */
export async function decideTeamRequest(
  id: number,
  approve: boolean,
  note: string,
  devUser?: string,
): Promise<TeamMembershipRequest> {
  const r = await check(
    await fetch(`${API_BASE}/api/teams/requests/${id}/decide`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await authHeaders(devUser)),
      },
      body: JSON.stringify({ approve, note }),
    }),
  );
  return ((await r.json()) as { request: TeamMembershipRequest }).request;
}

export function grantTeamClearance(
  slug: string,
  body: { token: string; data_owner: string; note?: string },
  devUser?: string,
): Promise<TeamsResponse> {
  return teamsFetch(
    `/${encodeURIComponent(slug)}/clearances`,
    { method: "POST", body: JSON.stringify(body) },
    devUser,
  );
}

export function revokeTeamClearance(
  slug: string,
  token: string,
  devUser?: string,
): Promise<TeamsResponse> {
  return teamsFetch(
    `/${encodeURIComponent(slug)}/clearances/${encodeURIComponent(token)}`,
    { method: "DELETE" },
    devUser,
  );
}

// ── Logs (ADR 0041) ────────────────────────────────────────────────────────

export interface LogRecord {
  ts: string;
  level: string;
  service: string;
  logger: string;
  msg: string;
  request_id: string;
  revision: string;
  /** Non-canonical fields the emitting service added (doc_id, duration_ms, …). */
  fields: Record<string, unknown>;
}

export interface LogsResponse {
  /** "log_analytics" on Azure, "elasticsearch" on-prem. */
  backend: string;
  since: string;
  until: string;
  count: number;
  limit: number;
  /** True when the result hit `limit` — narrow the filters to see the rest. */
  truncated: boolean;
  records: LogRecord[];
}

export interface LogFilters {
  /** ISO-8601 or a relative offset the backend parses: "-15m", "-2h", "-7d". */
  since?: string;
  until?: string;
  level?: string;
  /** Substring match against the raw line; also finds non-JSON container output. */
  contains?: string;
  limit?: number;
  service?: string;
  requestId?: string;
  docId?: string;
  jobId?: string;
  assistantSlug?: string;
  status?: string;
  errorType?: string;
}

export async function getLogs(
  filters: LogFilters,
  devUser?: string,
): Promise<LogsResponse> {
  const qs = new URLSearchParams();
  const map: Record<string, string | undefined> = {
    since: filters.since,
    until: filters.until,
    level: filters.level,
    contains: filters.contains,
    limit: filters.limit ? String(filters.limit) : undefined,
    service: filters.service,
    request_id: filters.requestId,
    doc_id: filters.docId,
    job_id: filters.jobId,
    assistant_slug: filters.assistantSlug,
    status: filters.status,
    error_type: filters.errorType,
  };
  for (const [k, v] of Object.entries(map)) if (v) qs.set(k, v);
  const r = await check(
    await fetch(`${API_BASE}/api/admin/logs?${qs.toString()}`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as LogsResponse;
}

// ── Retention ──────────────────────────────────────────────────────────────

export interface PurgeResult {
  purged: number;
  retention_days: number;
}

export async function purgeRetention(devUser?: string): Promise<PurgeResult> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/retention/purge`, {
      method: "POST",
      headers: await authHeaders(devUser),
    }),
  );
  return (await r.json()) as PurgeResult;
}

// ── DOIT AI Board: assistant-request queue ─────────────────────────────

export interface AiRequest {
  id: number;
  requester_oid: string;
  requester_email: string;
  proposed_slug: string;
  proposed_name: string;
  use_case: string;
  data_sources: string;
  target_audience: string;
  business_case: string;
  status: string;
  notes: string;
  decided_by: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
}

export async function listAiRequests(
  devUser?: string,
  status?: string,
): Promise<AiRequest[]> {
  const url = status
    ? `${API_BASE}/api/admin/ai-requests?status_=${encodeURIComponent(status)}`
    : `${API_BASE}/api/admin/ai-requests`;
  const r = await check(
    await fetch(url, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as AiRequest[];
}

/** Fetch the agent.yaml scaffold for an approved AI request and trigger
 * a browser download. The backend renders the scaffold from the live
 * request row — no client-side composition. We re-create the filename
 * from `Content-Disposition` when present so multi-request workflows
 * don't collide on `download.yaml`. */
export async function downloadAiRequestScaffold(
  id: number,
  devUser?: string,
): Promise<void> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/ai-requests/${id}/scaffold`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  const blob = await r.blob();
  const disposition = r.headers.get("Content-Disposition") ?? "";
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match?.[1] ?? `ai-request-${id}-scaffold.yaml`;
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
}

export async function updateAiRequest(
  id: number,
  body: { status?: string; notes?: string },
  devUser?: string,
): Promise<AiRequest> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/ai-requests/${id}`, {
      method: "PATCH",
      headers: {
        ...(await authHeaders(devUser)),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }),
  );
  return (await r.json()) as AiRequest;
}

// ── Feedback inbox (what came in, before anyone analyzes it) ───────────────

/** One thumbs-up/down vote joined with the turn it rates. Mirrors
 *  `store.list_all_feedback` plus the `is_new` flag the endpoint stamps. */
export interface FeedbackRow {
  id: number;
  kind: string;
  comment: string | null;
  created_at: string;
  updated_at: string;
  message_id: number;
  message_content: string;
  conversation_id: number;
  conversation_title: string;
  assistant_slug: string;
  user_email: string | null;
  user_display_name: string | null;
  prior_user_content: string;
  /** Nobody has dealt with this row — no analyze run has covered it and
   * no admin has marked it reviewed by hand. */
  is_new: boolean;
  /** Set when an admin marked it dealt with outside the loop (ADR 0056). */
  reviewed_at: string | null;
  reviewed_by: string;
  /** The reply the author was sent from the service mailbox, if any. */
  replied_at?: string | null;
  replied_by?: string;
  reply_subject?: string;
  reply_text?: string | null;
}

/**
 * Mark feedback dealt with, or put it back.
 *
 * With `feedback_id`, one row. Without it, every row matching the filters —
 * pass exactly what the inbox is showing, so "mark all" means the list on
 * screen rather than the whole table.
 */
export async function markFeedbackReviewed(
  body: {
    feedback_id?: number;
    assistant_slug?: string;
    kind?: string;
    since?: string;
    reviewed?: boolean;
  },
  devUser?: string,
): Promise<{ changed: number; reviewed: boolean }> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/feedback/reviewed`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await authHeaders(devUser)),
      },
      body: JSON.stringify(body),
    }),
  );
  return (await r.json()) as { changed: number; reviewed: boolean };
}

export interface FeedbackCounts {
  total: number;
  down: number;
  up: number;
  new_total: number;
  new_down: number;
  new_up: number;
}

export interface FeedbackOverviewAssistant extends FeedbackCounts {
  slug: string;
  name: string;
  /** Newest feedback for this assistant, ISO. */
  latest_at: string | null;
  /** When the last finished analyze run started, ISO. Null = never. */
  last_reviewed_at: string | null;
}

export interface FeedbackOverview {
  since: string;
  generated_at: string;
  totals: FeedbackCounts;
  assistants: FeedbackOverviewAssistant[];
}

/** Counts only — cheap enough to poll, and never leaves a run row behind. */
export async function getFeedbackOverview(
  since?: string,
  devUser?: string,
): Promise<FeedbackOverview> {
  const qs = new URLSearchParams();
  if (since) qs.set("since", since);
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/feedback/overview${qs.toString() ? `?${qs}` : ""}`,
      { headers: await authHeaders(devUser), cache: "no-store" },
    ),
  );
  return (await r.json()) as FeedbackOverview;
}

export async function listFeedback(
  filters: {
    assistantSlug?: string;
    kind?: "up" | "down";
    since?: string;
    limit?: number;
  },
  devUser?: string,
): Promise<{ count: number; new_count: number; rows: FeedbackRow[] }> {
  const qs = new URLSearchParams();
  if (filters.assistantSlug) qs.set("assistant_slug", filters.assistantSlug);
  if (filters.kind) qs.set("kind", filters.kind);
  if (filters.since) qs.set("since", filters.since);
  if (filters.limit) qs.set("limit", String(filters.limit));
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/feedback${qs.toString() ? `?${qs}` : ""}`,
      { headers: await authHeaders(devUser), cache: "no-store" },
    ),
  );
  return (await r.json()) as {
    count: number;
    new_count: number;
    rows: FeedbackRow[];
  };
}

// ── Feedback Review (analyze → suggest → apply → retest loop) ──────────────

export type FeedbackReviewStatus =
  | "pending"
  | "analyzing"
  | "ready"
  | "applying"
  | "applied"
  | "retesting"
  | "complete"
  | "error";

export interface FeedbackReviewRunSummary {
  id: number;
  assistant_slug: string;
  status: FeedbackReviewStatus;
  applied_at: string | null;
  created_at: string;
  updated_at: string;
  n_negative: number;
  n_clusters: number;
}

export interface FeedbackReviewCluster {
  id: string;
  title: string;
  description: string;
  feedback_ids: number[];
  root_cause_hypothesis: string;
  suggested_fix: string;
}

export interface FeedbackReviewRow {
  feedback_id: number;
  comment: string;
  user_question: string;
  assistant_answer: string;
  user_email?: string;
  updated_at?: string;
}

export interface FeedbackReviewVerdict {
  feedback_id: number;
  question: string;
  original_complaint: string;
  original_answer: string;
  new_answer: string;
  fixed: boolean;
  improvement: number;
  explanation: string;
  errored?: boolean;
}

export interface FeedbackReviewRun {
  id: number;
  assistant_slug: string;
  status: FeedbackReviewStatus;
  payload: {
    since?: string;
    limit?: number;
    error_message?: string;
    rows?: FeedbackReviewRow[];
    clusters?: FeedbackReviewCluster[];
    current_prompt?: string;
    suggestion?: {
      new_system_prompt: string;
      rationale: string;
      out_of_scope: string[];
    };
    verdicts?: FeedbackReviewVerdict[];
    n_negative?: number;
    n_fixed?: number;
    n_retested?: number;
  };
  applied_at: string | null;
  applied_by_oid: string | null;
  created_at: string;
  updated_at: string;
}

export async function createFeedbackReviewRun(
  slug: string,
  body: { since: string; limit: number },
  devUser?: string,
): Promise<{ run_id: number; status: FeedbackReviewStatus }> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/feedback-review/${slug}/runs`, {
      method: "POST",
      headers: {
        ...(await authHeaders(devUser)),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }),
  );
  return (await r.json()) as { run_id: number; status: FeedbackReviewStatus };
}

export async function listFeedbackReviewRuns(
  slug: string,
  devUser?: string,
): Promise<FeedbackReviewRunSummary[]> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/feedback-review/${slug}/runs`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  const j = await r.json();
  return j.runs as FeedbackReviewRunSummary[];
}

export async function getFeedbackReviewRun(
  runId: number,
  devUser?: string,
): Promise<FeedbackReviewRun> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/feedback-review/runs/${runId}`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as FeedbackReviewRun;
}

export async function applyFeedbackReviewPrompt(
  runId: number,
  systemPrompt: string,
  devUser?: string,
): Promise<void> {
  await check(
    await fetch(`${API_BASE}/api/admin/feedback-review/runs/${runId}/apply`, {
      method: "POST",
      headers: {
        ...(await authHeaders(devUser)),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ system_prompt: systemPrompt }),
    }),
  );
}

export async function kickFeedbackReviewRetest(
  runId: number,
  devUser?: string,
): Promise<void> {
  await check(
    await fetch(`${API_BASE}/api/admin/feedback-review/runs/${runId}/retest`, {
      method: "POST",
      headers: await authHeaders(devUser),
    }),
  );
}

export interface BetaSignupRow {
  email: string;
  name: string;
  note: string;
  /** True when the request carried a valid Entra token — i.e. a colleague
   *  who is signed in but not entitled, rather than an outside visitor. */
  identified: boolean;
  source: string;
  created_at: string | null;
}

export async function getBetaSignups(
  devUser?: string,
): Promise<BetaSignupRow[]> {
  const r = await check(
    await fetch(`${API_BASE}/api/admin/beta-signups`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  const body = (await r.json()) as { signups?: BetaSignupRow[] };
  return body.signups ?? [];
}

// ── AI governance (ADR 0050) ───────────────────────────────────────────────

/** One control's computed state. `na` = the control does not apply to this
 * scope (e.g. a platform-only control listed against an assistant). */
export type ControlStatus = "ok" | "warn" | "gap" | "na";

/** EU AI Act classification. `unclassified` is a gap, not a default. */
export type RiskClass = "unclassified" | "minimal" | "limited" | "high";

export type PiiRedactionMode = "inherit" | "off" | "regex" | "azure";
export type SensitiveInputMode = "inherit" | "warn" | "block" | "off";

/** Inventory hygiene flags the backend raises per assistant. */
export type GovernanceFlag =
  | "no_owner"
  | "review_overdue"
  | "unclassified"
  | "review_unset"
  /** Live config was edited in the admin UI and no longer matches the
   * checked-in YAML that change management reviewed. */
  | "config_drift"
  /** Group-gated, and no data owner has confirmed the audience — or the
   * group / sources changed since they did (ADR 0053). */
  | "access_unattested"
  /** The AI Board has not accepted it for use (ADR 0066). */
  | "unapproved"
  /** The running config no longer matches the accepted one. */
  | "changed_since_approval"
  /** Accepted, but more than a year ago: due for review. */
  | "acceptance_expired"
  /** Reads restricted data and no test checks that someone is kept out. */
  | "tests_missing"
  /** The last run has a failing or inconclusive check. */
  | "tests_failing";

export interface GovernanceDeployment {
  pii_redact_enabled: boolean;
  pii_mode: string;
  /** false = monitor mode: detected and recorded, not masked. */
  pii_enforce?: boolean;
  pii_fail_mode: string;
  sensitive_input_check: boolean;
  budget_enabled: boolean;
  budget_enforced: boolean;
  retention_days: number;
  inactivity_delete_days: number;
  /** 0 = audit rows are kept forever. */
  audit_retention_days: number;
  telemetry: {
    app_insights: boolean;
    otlp_endpoint: boolean;
    capture_content: boolean;
  };
  retriever_backend: string;
  llm_provider: string;
}

/** Catalogue entry from `config/controls.yaml` — what a control is and which
 * framework clauses it maps to. Status lives separately (per scope). */
export interface GovernanceControl {
  id: string;
  title: string;
  /** Two or three words for a chip. Falls back to `title` server-side. */
  short?: string;
  summary: string;
  scope: "platform" | "assistant";
  /** How the control is evidenced: by construction, a setting, the turn
   * ledger, the audit log, or the inventory itself. */
  evidence: "construction" | "setting" | "ledger" | "audit" | "inventory";
  /** framework key (eu_ai_act, iso_42001, gdpr, owasp_llm) → clause refs. */
  frameworks: Record<string, string[]>;
}

export interface ControlStatusRow {
  id: string;
  status: ControlStatus;
  detail: string;
}

/** The editable classification block (typed assistant settings, ADR 0030). */
export interface GovernanceFields {
  risk_class: RiskClass;
  risk_rationale: string;
  data_classes: string[];
  lawful_basis: string;
  assessment_ref: string;
  intended_use: string;
  out_of_scope_uses: string;
  /** ISO dates (YYYY-MM-DD) or null when never set. */
  last_reviewed_at: string | null;
  next_review_at: string | null;
  notes: string;
}

export interface GuardrailFields {
  pii_redaction: PiiRedactionMode;
  sensitive_input: SensitiveInputMode;
  /** Read-only here — abstention and validation are tuned on the assistant. */
  abstain_enabled: boolean;
  abstain_threshold: number;
  validation_enabled: boolean;
}

/** Ledger counts over `GovernanceResponse.window_days`. */
export interface GovernanceCounts {
  turns: number;
  errors: number;
  turns_with_evidence: number;
  pii_redacted_turns: number;
  sensitive_input_turns: number;
  abstained: number;
  citations_dropped: number;
}

/** Who signed off on the assistant's running configuration, and whether it
 * has changed since (ADR 0050). "Reviewed" used to mean "matches the
 * checked-in YAML", which made every admin-UI edit look like drift; it now
 * means a named person pressed Approve in the product.
 *
 * Every field is optional: a backend that predates approval omits the whole
 * object, and an assistant nobody has approved yet carries only
 * `fingerprint` — which is ALWAYS the CURRENT config's fingerprint, not the
 * approved one. */
export interface ApprovalState {
  /** False = never approved; the attribution fields are then empty. */
  approved?: boolean;
  /** ADR 0066 — an AI Board acceptance (self or recorded). A pre-0066
   * admin approval is `approved` but not `accepted`. */
  accepted?: boolean;
  /** The running config is the accepted one (regardless of age). */
  matches?: boolean;
  /** True = matches, and not older than the review interval. */
  current?: boolean;
  expired?: boolean;
  valid_until?: string | null;
  /** "self" | "recorded" | "admin" (pre-ADR 0066) | "" (never). */
  method?: string;
  accepted_as?: string;
  approved_by?: string | null;
  approved_by_email?: string | null;
  approved_by_name?: string;
  /** The admin who recorded it, for a `recorded` acceptance. */
  recorded_by_email?: string | null;
  approved_at?: string | null;
  note?: string;
  /** Short hash of the configuration this row is talking about. */
  fingerprint?: string;
}

/** ADR 0053 — derived, never entered. `open` = no sensitive data;
 * `per_person` = sensitive, and every source limits each person to what they
 * may see; `group_gated` = sensitive, and for at least one source being in
 * the group IS the access decision. */
export type AccessClassKey =
  | "open"
  | "per_person"
  | "group_gated"
  | "undetermined";

export interface AccessClassSource {
  kind: "corpus" | "unpartitioned" | "table" | "tool";
  name: string;
  /** `mirrored` = the source system's own permissions; `assigned` = per
   * person, but the mapping is kept here (ADR 0054 §7); `own` = the
   * caller's own data. */
  enforcement:
    | "mirrored"
    | "assigned"
    | "own"
    | "group"
    | "open"
    | "undeclared";
  /** Present when the enforcement was derived rather than read from the
   * reviewed tag registry — e.g. "from 2 sources added in Data sources". */
  basis?: string;
  /** Corpus or table: the groups it is stamped with (from the index). */
  groups?: string[];
  /** Group-gated part: do this assistant's own users get in? `kept_out`:
   * the assistant holds it, but only people personally in its group see it. */
  reach?: "reached" | "kept_out";
}

/** A data owner's statement that everyone in the assistant's group already
 * has full access in the source system. Bound to the group and the
 * group-gated sources; `current` goes false when either changes. */
export interface AccessAttestation {
  attested: boolean;
  current: boolean;
  data_owner: string;
  recorded_by: string | null;
  recorded_by_email: string | null;
  recorded_at: string | null;
  note: string;
  /** ADR 0066 — confirmations last a year. */
  expired?: boolean;
  valid_until?: string | null;
  /** Aggregate only: the data sources still waiting for their owner. */
  pending_sources?: string[];
}

/** A data owner's confirmation for ONE data source this assistant reads
 * restricted parts of — the SharePoint owner speaks for SharePoint only. */
export interface SourceAttestation extends AccessAttestation {
  source_key: string;
  source: string;
  /** The restricted parts of that source (scope tags, table names). */
  parts: string[];
  /** "assistant": still covered by an older all-in-one confirmation. */
  via?: string;
}

export interface AccessClass {
  key: AccessClassKey;
  label: string;
  /** Who decides what for this class, in sentences. */
  route: { new_assistant: string; user_access: string };
  detail: string;
  /** Set when the declared data classes look too low for the sources. */
  note: string;
  sources: AccessClassSource[];
}

export interface GovernanceAssistant {
  slug: string;
  name: string;
  enabled: boolean;
  owner_oid: string | null;
  owner_email: string | null;
  /** ADR 0038 amendment — further accountable people, as e-mails where
   * the platform knows them, else OIDs. */
  co_owner_oids?: string[];
  co_owner_emails?: string[];
  maturity: string;
  acl_scope: string[];
  content_scope: string[];
  governance: GovernanceFields;
  /** Absent on a backend that predates ADR 0053. */
  access_class?: AccessClass;
  access_attestation?: AccessAttestation;
  access_attestations?: SourceAttestation[];
  /** Teams granted access (ADR 0063); part of what a data owner confirms. */
  teams?: { slug: string; name: string; members: number }[];
  guardrails: GuardrailFields;
  controls: ControlStatusRow[];
  counts: GovernanceCounts;
  /** Fields whose live value differs from the reviewed YAML; empty when
   * the running config matches. */
  config_drift: string[];
  /** Absent on a backend that predates in-product approval. */
  approval?: ApprovalState;
  /** ADR 0066 — absent on an older backend. */
  access_tests?: AccessTestsSummary;
  readiness?: Readiness;
  flags: GovernanceFlag[];
}

// ── Access tests and acceptance (ADR 0066) ────────────────────────────────

export type StepStatus = "ok" | "na" | "todo";

export interface ReadinessStep {
  key: "data_owners" | "tests" | "accepted";
  label: string;
  status: StepStatus;
  detail: string;
}

/** The three steps before an assistant may be used. */
export interface Readiness {
  ready: boolean;
  /** Steps 1 and 2 are done and the record is filled in. */
  can_accept: boolean;
  /** What the AI Board reads, not yet filled in: owner, risk class … */
  record_missing?: string[];
  steps: ReadinessStep[];
}

export type AccessTestsState =
  | "not_needed"
  | "missing"
  | "not_run"
  | "stale"
  | "failing"
  | "inconclusive"
  | "passing";

export interface AccessTestRunInfo {
  id: number;
  ran_at: string;
  trigger: string;
  ran_by: string;
  passed: number;
  failed: number;
  inconclusive: number;
}

export interface AccessTestsSummary {
  state: AccessTestsState;
  ok: boolean;
  detail: string;
  tests: number;
  run: AccessTestRunInfo | null;
}

export type Expect = "sees" | "blocked";

export interface AccessTestPersona {
  /** "everyone" | "team:<slug>" | "group:<name>" */
  as: string;
  expect: Expect;
}

export interface AccessTest {
  id: number;
  assistant_slug: string;
  question: string;
  /** Text in a document's title or path, or `table:<name>`. */
  look_for: string;
  source_key: string;
  personas: AccessTestPersona[];
  note: string;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
}

export type AccessOutcome =
  | "sees"
  | "not_found"
  | "cannot_open"
  | "unknown_persona";

export interface AccessTestResult {
  test_id: number;
  question: string;
  look_for: string;
  source_key: string;
  persona: string;
  persona_label: string;
  expect: Expect;
  outcome: AccessOutcome;
  /** How they got in, or why not. */
  why: string;
  /** What the admin control found: "sees" makes the check meaningful. */
  control: AccessOutcome;
  verdict: "pass" | "fail" | "inconclusive";
  /** Admins only: titles of what matched. */
  found?: string[];
}

export interface PersonaOption {
  as: string;
  label: string;
  /** Whether this persona may open the assistant today. */
  may_use: boolean;
}

export interface AccessTestsView {
  slug: string;
  name: string;
  summary: AccessTestsSummary;
  readiness: Readiness;
  tests: AccessTest[];
  run: (AccessTestRunInfo & { results: AccessTestResult[] }) | null;
  personas: PersonaOption[];
}

export interface AccessTestBody {
  question: string;
  look_for: string;
  personas: AccessTestPersona[];
  note?: string;
  source_key?: string;
}

const _slugUrl = (slug: string, rest = "") =>
  `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/access-tests${rest}`;

async function _jsonCall<T>(
  url: string,
  method: string,
  body: unknown,
  devUser?: string,
): Promise<T> {
  const r = await check(
    await fetch(url, {
      method,
      headers: {
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        ...(await authHeaders(devUser)),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      cache: "no-store",
    }),
  );
  return (await r.json()) as T;
}

export function getAccessTests(
  slug: string,
  devUser?: string,
): Promise<AccessTestsView> {
  return _jsonCall(_slugUrl(slug), "GET", undefined, devUser);
}

export function saveAccessTest(
  slug: string,
  id: number | null,
  body: AccessTestBody,
  devUser?: string,
): Promise<AccessTestsView> {
  return id === null
    ? _jsonCall(_slugUrl(slug), "POST", body, devUser)
    : _jsonCall(_slugUrl(slug, `/${id}`), "PUT", body, devUser);
}

export function retireAccessTest(
  slug: string,
  id: number,
  devUser?: string,
): Promise<AccessTestsView> {
  return _jsonCall(_slugUrl(slug, `/${id}`), "DELETE", undefined, devUser);
}

export function runAccessTests(
  slug: string,
  devUser?: string,
): Promise<AccessTestsView> {
  return _jsonCall(_slugUrl(slug, "/run"), "POST", {}, devUser);
}

export interface SuggestedTest {
  test: AccessTestBody & { note: string };
  /** The proposal already run once, as it would be saved. */
  results: AccessTestResult[];
}

/** Tests drawn from the assistant's own documents and tables — not saved. */
export function suggestAccessTests(
  slug: string,
  devUser?: string,
): Promise<{ suggestions: SuggestedTest[] }> {
  return _jsonCall(_slugUrl(slug, "/suggest"), "POST", {}, devUser);
}

export interface PersonaAnswer {
  persona: string;
  persona_label: string;
  expect: Expect;
  answer: string;
  citations?: string[];
  /** Set instead of an answer: "Cannot open this assistant." */
  note?: string;
}

/** Ask the question as each persona — real model turns, not stored. */
export function getAccessTestAnswers(
  slug: string,
  id: number,
  devUser?: string,
): Promise<{ test: AccessTest; answers: PersonaAnswer[] }> {
  return _jsonCall(_slugUrl(slug, `/${id}/answers`), "POST", {}, devUser);
}

// ── Review rounds (ADR 0066) ──────────────────────────────────────────────

export interface ReviewRound {
  id: number;
  title: string;
  opened_at: string;
  opened_by: string;
  due_on: string;
  closed_at: string | null;
  closed_by: string;
  held_on: string;
  attendees: string;
  notes: string;
  has_report: boolean;
}

export interface ReviewItem {
  kind: "confirm" | "tests" | "accept" | "agreement";
  slug?: string;
  source_key?: string;
  what: string;
  who: string;
  done: boolean;
  /** accept only: steps 1 and 2 are done. */
  ready?: boolean;
}

export interface ReviewPackAssistant {
  slug: string;
  name: string;
  maturity: string;
  audience: string[];
  restricted_sources: string[];
  access_class: string;
  risk_class: string;
  intended_use: string;
  steps: ReadinessStep[];
  accepted: boolean;
  accepted_by: string;
  accepted_at: string | null;
  valid_until: string | null;
  tests: {
    state: AccessTestsState;
    detail: string;
    count: number;
    passed: number;
    failed: number;
  };
}

export interface ReviewPack {
  generated_at: string;
  round: ReviewRound | null;
  review_days: number;
  items: ReviewItem[];
  done: number;
  total: number;
  assistants: ReviewPackAssistant[];
  sources: {
    key: string;
    label: string;
    system: string;
    agreement_state: string;
    signatures: string;
    data_owner: string;
  }[];
  people: { role: string; people: string }[];
}

export interface ReviewRoundsResponse {
  open: ReviewRound | null;
  pack: ReviewPack;
  rounds: ReviewRound[];
}

export function getReviewRounds(devUser?: string): Promise<ReviewRoundsResponse> {
  return _jsonCall(`${API_BASE}/api/admin/review-rounds`, "GET", undefined, devUser);
}

export function startReviewRound(
  body: { title?: string; due_on?: string },
  devUser?: string,
): Promise<ReviewRoundsResponse> {
  return _jsonCall(`${API_BASE}/api/admin/review-rounds`, "POST", body, devUser);
}

export function closeReviewRound(
  id: number,
  body: { held_on: string; attendees: string; notes: string },
  devUser?: string,
): Promise<ReviewRoundsResponse> {
  return _jsonCall(
    `${API_BASE}/api/admin/review-rounds/${id}/close`,
    "POST",
    body,
    devUser,
  );
}

/** The live pack (id null) or a closed round's report, as Markdown. */
export async function getReviewMarkdown(
  id: number | null,
  devUser?: string,
): Promise<Blob> {
  const url =
    id === null
      ? `${API_BASE}/api/admin/review-pack.md`
      : `${API_BASE}/api/admin/review-rounds/${id}/report.md`;
  const r = await check(
    await fetch(url, {
      headers: { Accept: "text/markdown", ...(await authHeaders(devUser)) },
      cache: "no-store",
    }),
  );
  return await r.blob();
}

export interface GovernanceResponse {
  generated_at: string;
  window_days: number;
  deployment: GovernanceDeployment;
  controls: GovernanceControl[];
  platform_controls: ControlStatusRow[];
  assistants: GovernanceAssistant[];
}

/** PUT body: any subset of the governance fields, plus the two guardrails
 * that are editable from this tab. */
export interface GovernanceSaveBody extends Partial<GovernanceFields> {
  /** The owner by e-mail (or an Entra object id); resolved server-side. */
  owner_email?: string;
  /** Co-owners by e-mail (or object id). Sent only when they changed. */
  co_owner_emails?: string[];
  guardrails?: Partial<
    Pick<GuardrailFields, "pii_redaction" | "sensitive_input">
  >;
}

/** Content-free per-turn evidence. Every key is optional — older ledger
 * rows and non-RAG turns carry fewer of them. */
export interface GovernanceTurnEvidence {
  request_id?: string;
  transport?: string;
  prompt_sha256?: string;
  hits_returned?: number;
  retrieved_chunk_ids?: string[];
  pii_backend?: string;
  pii_redacted_count?: number;
  sensitive_input_kinds?: string[];
  abstained?: boolean;
  faithfulness_min_support?: number;
  citations_total?: number;
  citations_dropped?: number;
  tool_names?: string[];
}

export interface GovernanceTurnRow {
  ts: string;
  user_email: string | null;
  assistant_slug: string;
  model: string;
  status: "ok" | "error";
  latency_ms: number;
  evidence: GovernanceTurnEvidence;
}

export interface GovernanceTurnsResponse {
  rows: GovernanceTurnRow[];
}

export async function getGovernance(
  devUser?: string,
  /**
   * Evidence window in days. The per-assistant FLAGS do not depend on it —
   * owner, classification, review date and sign-off are properties of the
   * row — but the control statuses do, and computing them scans the turn
   * ledger. A caller that only wants the flags (the Overview) should pass 1
   * rather than make the landing page wait on a 30-day scan.
   */
  days?: number,
): Promise<GovernanceResponse> {
  const q = days ? `?days=${days}` : "";
  const r = await check(
    await fetch(`${API_BASE}/api/admin/governance${q}`, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as GovernanceResponse;
}

export async function saveGovernance(
  slug: string,
  body: GovernanceSaveBody,
  devUser?: string,
): Promise<GovernanceAssistant> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/governance/${encodeURIComponent(slug)}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders(devUser)),
        },
        body: JSON.stringify(body),
      },
    ),
  );
  return (await r.json()) as GovernanceAssistant;
}

/** Record, in an AI Board member's name, that they accepted the assistant
 * for use as it runs now (ADR 0066). `acceptedBy` is their e-mail or name
 * as listed under Responsibilities; `note` says where the decision is
 * written down. Returns the refreshed assistant entry. */
export async function approveGovernanceConfig(
  slug: string,
  acceptedBy: string,
  note: string,
  devUser?: string,
): Promise<GovernanceAssistant> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/governance/${encodeURIComponent(slug)}/approve`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders(devUser)),
        },
        body: JSON.stringify({ accepted_by: acceptedBy, note }),
      },
    ),
  );
  return (await r.json()) as GovernanceAssistant;
}

// ── Assistant members (ADR 0054) ──────────────────────────────────────────

export interface AssistantMember {
  member_oid: string;
  member_email: string;
  display_name: string;
  /** Partitions this person is narrowed to, e.g. `{"risma-orgs": ["117"]}`. */
  scopes: Record<string, string[]>;
  /** Who agreed to it outside this system — the data owner, for a
   * group-gated assistant. */
  approved_by: string;
  note: string;
  granted_by: string;
  granted_by_email: string;
  granted_at: string | null;
  expires_at: string | null;
  expired: boolean;
}

export interface MemberScopeDef {
  name: string;
  label: string;
  options: { key: string; label: string }[];
}

export interface AssistantMembersResponse {
  slug: string;
  acl_scope: string[];
  maturity: string;
  access_class: AccessClass;
  /** True for a group-gated assistant: every grant must name an approver. */
  approval_required: boolean;
  scopes: MemberScopeDef[];
  members: AssistantMember[];
}

export interface MemberGrant {
  member_email?: string;
  member_oid?: string;
  scopes?: Record<string, string[]>;
  approved_by?: string;
  note?: string;
  expires_at?: string | null;
}

export async function listAssistantMembers(
  slug: string,
  devUser?: string,
): Promise<AssistantMembersResponse> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/members`,
      { headers: await authHeaders(devUser), cache: "no-store" },
    ),
  );
  return (await r.json()) as AssistantMembersResponse;
}

export async function grantAssistantMember(
  slug: string,
  body: MemberGrant,
  devUser?: string,
): Promise<AssistantMember> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/members`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders(devUser)),
        },
        body: JSON.stringify(body),
      },
    ),
  );
  return (await r.json()) as AssistantMember;
}

export async function revokeAssistantMember(
  slug: string,
  memberOid: string,
  devUser?: string,
): Promise<void> {
  await check(
    await fetch(
      `${API_BASE}/api/admin/assistants/${encodeURIComponent(slug)}/members/${encodeURIComponent(memberOid)}`,
      { method: "DELETE", headers: await authHeaders(devUser) },
    ),
  );
}

export async function attestGovernanceAccess(
  slug: string,
  /** `source_key`: confirm that data source only; omitted = every one. */
  body: { data_owner: string; note: string; source_key?: string },
  devUser?: string,
): Promise<GovernanceAssistant> {
  const r = await check(
    await fetch(
      `${API_BASE}/api/admin/governance/${encodeURIComponent(slug)}/attest`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(await authHeaders(devUser)),
        },
        body: JSON.stringify(body),
      },
    ),
  );
  return (await r.json()) as GovernanceAssistant;
}

export async function getGovernanceTurns(
  params: { assistant?: string; days?: number; limit?: number },
  devUser?: string,
): Promise<GovernanceTurnsResponse> {
  const qs = new URLSearchParams();
  if (params.assistant) qs.set("assistant", params.assistant);
  if (params.days) qs.set("days", String(params.days));
  if (params.limit) qs.set("limit", String(params.limit));
  const url = qs.toString()
    ? `${API_BASE}/api/admin/governance/turns?${qs.toString()}`
    : `${API_BASE}/api/admin/governance/turns`;
  const r = await check(
    await fetch(url, {
      headers: await authHeaders(devUser),
      cache: "no-store",
    }),
  );
  return (await r.json()) as GovernanceTurnsResponse;
}

/** The evidence export (`json`) or the quarterly review pack (`md`) as a
 * Blob, so the caller can hand it to the browser as a download. The body is
 * not parsed here: it is meant for a GRC tool or a meeting, not this UI. */
export async function getGovernanceReport(
  format: "json" | "md",
  days: number,
  devUser?: string,
): Promise<Blob> {
  const qs = new URLSearchParams({ format, days: String(days) });
  const r = await check(
    await fetch(`${API_BASE}/api/admin/governance/report?${qs.toString()}`, {
      headers: {
        Accept: format === "json" ? "application/json" : "text/markdown",
        ...(await authHeaders(devUser)),
      },
      cache: "no-store",
    }),
  );
  return await r.blob();
}
